import { CalculatorIcon, PlusIcon, ShareIcon, TagIcon } from '@heroicons/react/20/solid';
import { ButtonV2, Text, TooltipV2 } from '@cimplico/components';
import { CustomWorksheetTemplateOverview } from '../../types/custom-worksheet-v2';
import { useCreateOtherSchedules } from '../../queries/use-create-other-schedule';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { Calculation, WorksheetTypeEnum } from '../../types/worksheet';
import { toastNotification } from '../../components/toast/Toast';
import { useNavigate } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import plural from '../../utilities/plural';

type CalculationTemplateCardProps = {
    template: CustomWorksheetTemplateOverview;
    disabled: boolean;
    calculation?: Calculation;
    onCreating: () => void;
    onCreate: () => void;
};
function CalculationTemplateCard({ template, disabled, calculation, onCreate, onCreating }: CalculationTemplateCardProps): JSX.Element {
    const { id } = useWorkpaperContext();
    const { shortId } = useFirmContext();
    const createQuery = useCreateOtherSchedules(id);
    const navigate = useNavigate();

    function create(): void {
        onCreating();
        createQuery.mutate(
            [
                {
                    type: WorksheetTypeEnum.GENERIC,
                    calculationTemplate: template,
                },
            ],
            {
                onSettled: () => {
                    onCreate();
                },
                onSuccess: (res) => {
                    const worksheetId = res.results[0].data?.id;
                    if (worksheetId) {
                        navigate(`/${shortId}/workpapers/${id}/other-schedules/${worksheetId}`);
                    } else {
                        toastNotification(
                            'error',
                            'Successfully created schedule, but unable to redirect.',
                            'Please navigate to the other schedules table.'
                        );
                    }
                },
                onError: (err) => {
                    toastNotification(
                        'error',
                        err.response?.data?.results?.at(0)?.error?.fields?.name ?? 'Something went wrong! Unable to create other schedule.'
                    );
                },
            }
        );
    }

    return (
        <div key={template.id} className="p-4 border rounded relative">
            <div className="flex flex-col h-full gap-2">
                <div className="flex items-center gap-1">
                    <CalculatorIcon className="h-4 w-4 text-green-600 shrink-0" />
                    <Text className="text-sm font-bold text-gray-800 ml-1">{template.name}</Text>
                </div>
                {template.description && (
                    <Text className="text-xs text-gray-500" lines={2}>
                        {template.description}
                    </Text>
                )}
                <div className="flex items-start gap-2 border-t pt-2 mt-auto">
                    {/* Add tags */}
                    <div>
                        <div className="flex flex-wrap gap-1 items-center">
                            <TagIcon className="h-4 w-4 text-gray-600" />
                            {template.tags.slice(0, 1).map((tag) => (
                                <label key={tag} className="text-xs text-gray-500">
                                    {tag}
                                </label>
                            ))}
                            {template.tags.length > 1 && (
                                <span className="-mt-1">
                                    <TooltipV2 message={template.tags.slice(1)}>
                                        <label className="text-xs text-gray-800">+{template.tags.length - 1}</label>
                                    </TooltipV2>
                                </span>
                            )}
                        </div>
                    </div>
                    <span className="ml-auto">
                        <ButtonV2
                            size="xs"
                            label="Create"
                            leftIcon={<PlusIcon />}
                            type="secondary"
                            onClick={create}
                            isDisabled={disabled}
                            isLoading={createQuery.isLoading}
                        />
                    </span>
                </div>
                {calculation && (
                    <div className="flex items-center gap-1">
                        <ShareIcon className="text-gray-600 h-4 w-4" />
                        <label className="text-xs text-gray-500">
                            <TooltipV2 message={calculation.worksheets.map((ws) => ws.name)}>
                                {calculation.worksheets.length} {plural(calculation.worksheets.length, 'worksheet')}
                            </TooltipV2>
                        </label>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CalculationTemplateCard;
